import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getSocket } from '../lib/socket';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';

/**
 * Connects the socket when the user is authenticated and wires up host-specific
 * event handlers. Call once at the root of the authenticated view (AppShell).
 *
 * Events handled:
 *   guest_request:new              → toast + prepend to ['service-requests'] cache
 *   service_request:status_updated → toast + in-place status update in cache
 *   connect_error (auth failure)   → logout
 */
export const useSocket = () => {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Keep logout in a ref so the connect_error handler never captures a stale copy
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  useEffect(() => {
    if (!user) return;

    const socket = getSocket();

    const onConnectError = (err) => {
      const msg = (err?.message ?? '').toLowerCase();
      const isAuthError =
        msg.includes('token') ||
        msg.includes('jwt') ||
        msg.includes('auth') ||
        msg.includes('unauthorized') ||
        msg.includes('expired');
      if (isAuthError) logoutRef.current();
    };

    const onGuestRequestNew = ({ id, room_number, host_id, property_id, issue, service_type }) => {
      toast(`New guest request — Room ${room_number}: ${issue}`, 'info');

      queryClient.setQueryData(['service-requests'], (old) => {
        if (!old) return old;
        const entry = {
          id,
          room_number,
          host_id,
          property_id,
          issue,
          service_type,
          status: 'pending',
          is_approved: 0,
          created_at: new Date().toISOString(),
        };
        if (Array.isArray(old)) return [entry, ...old];
        if (old.serviceRequests) return { ...old, serviceRequests: [entry, ...old.serviceRequests] };
        return old;
      });
    };

    const onStatusUpdated = ({ id, status }) => {
      toast(`Request #${id} status changed to: ${status.replace(/_/g, ' ')}`, 'success');

      queryClient.setQueryData(['service-requests'], (old) => {
        if (!old) return old;
        const update = (list) => list.map((r) => (r.id === id ? { ...r, status } : r));
        if (Array.isArray(old)) return update(old);
        if (old.serviceRequests) return { ...old, serviceRequests: update(old.serviceRequests) };
        return old;
      });
    };

    socket.on('connect_error', onConnectError);
    socket.on('guest_request:new', onGuestRequestNew);
    socket.on('service_request:status_updated', onStatusUpdated);

    return () => {
      socket.off('connect_error', onConnectError);
      socket.off('guest_request:new', onGuestRequestNew);
      socket.off('service_request:status_updated', onStatusUpdated);
    };
  }, [user, toast, queryClient]);
};
